import {
  DEFAULT_SETTINGS,
  type GameSettings,
  type GuestSession
} from "./types";
import {
  createDefaultClubPlan,
  normalizeClubPlan,
  type ClubPlan
} from "../domain/club";
import type { MatchSnapshot, MatchSummary } from "../game/types";

const SESSION_KEY = "kipsball.session.v1";
const SETTINGS_KEY = "kipsball.settings.v1";
const CLUB_PLAN_KEY = "kipsball.club-plan.v1";
const DATABASE_NAME = "kipsball-local-v1";
const DATABASE_VERSION = 1;

function safeParse<T>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

export class Persistence {
  private databasePromise: Promise<IDBDatabase> | null = null;

  getSession(): GuestSession | null {
    const session = safeParse<GuestSession>(localStorage.getItem(SESSION_KEY));
    return session?.schemaVersion === 1 ? session : null;
  }

  saveSession(session: GuestSession): void {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  }

  getSettings(): GameSettings {
    const saved = safeParse<GameSettings>(localStorage.getItem(SETTINGS_KEY));
    return saved?.schemaVersion === 1
      ? { ...DEFAULT_SETTINGS, ...saved }
      : { ...DEFAULT_SETTINGS };
  }

  saveSettings(settings: GameSettings): void {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  }

  getClubPlan(): ClubPlan {
    const saved = safeParse<ClubPlan>(localStorage.getItem(CLUB_PLAN_KEY));
    return saved?.schemaVersion === 1
      ? normalizeClubPlan(saved)
      : createDefaultClubPlan();
  }

  saveClubPlan(plan: ClubPlan): void {
    localStorage.setItem(CLUB_PLAN_KEY, JSON.stringify(normalizeClubPlan(plan)));
  }

  async saveActiveMatch(snapshot: MatchSnapshot): Promise<void> {
    await this.put("activeMatch", snapshot);
  }

  async getActiveMatch(): Promise<MatchSnapshot | null> {
    return this.get<MatchSnapshot>("activeMatch");
  }

  async clearActiveMatch(): Promise<void> {
    const database = await this.openDatabase();
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction("state", "readwrite");
      transaction.objectStore("state").delete("activeMatch");
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async addMatchSummary(summary: MatchSummary): Promise<void> {
    const history = (await this.get<MatchSummary[]>("history")) ?? [];
    history.unshift(summary);
    await this.put("history", history.slice(0, 20));
  }

  async getHistory(): Promise<MatchSummary[]> {
    return (await this.get<MatchSummary[]>("history")) ?? [];
  }

  private async put(key: string, value: unknown): Promise<void> {
    const database = await this.openDatabase();
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction("state", "readwrite");
      transaction.objectStore("state").put(value, key);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  private async get<T>(key: string): Promise<T | null> {
    const database = await this.openDatabase();
    return new Promise<T | null>((resolve, reject) => {
      const transaction = database.transaction("state", "readonly");
      const request = transaction.objectStore("state").get(key);
      request.onsuccess = () => resolve((request.result as T | undefined) ?? null);
      request.onerror = () => reject(request.error);
    });
  }

  private openDatabase(): Promise<IDBDatabase> {
    if (this.databasePromise) return this.databasePromise;

    this.databasePromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains("state")) {
          database.createObjectStore("state");
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    return this.databasePromise;
  }
}
